import React, { useState, useEffect } from 'react';
import './styles/App.css';
import Header from './components/Header';
import BookmarkCard from './components/BookmarkCard';
import ConfirmationModal from './components/ConfirmationModal';
import LoadingSpinner from './components/LoadingSpinner';
import EmptyState from './components/EmptyState';
import ErrorMessage from './components/ErrorMessage';
import UserInfoDisplay from './components/UserInfoDisplay';
import { bookmarkService, urlUtils, textUtils } from './services/bookmarkService';
import { useTeamsContext } from './hooks/useTeamsContext';

function App() {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [bookmarkToDelete, setBookmarkToDelete] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Use Teams context hook
  const { userInfo, teamsLoading, teamsError } = useTeamsContext();

  useEffect(() => {
    // Only refresh bookmarks when Teams context is loaded (or failed to load)
    if (!teamsLoading) {
      refreshBookmarks();
    }
  }, [teamsLoading]);

  const getUserAndTenantId = () => {
    const urlParams = urlUtils.getUrlParams();
    return urlUtils.getUserAndTenantId(urlParams, userInfo);
  };

  const refreshBookmarks = async () => {
    setIsRefreshing(true);
    setLoading(true);
    setError('');

    const { userId, tenantId } = getUserAndTenantId();
    
    try {
      const bookmarkData = await bookmarkService.fetchBookmarks(userId, tenantId);
      
      if (bookmarkData.success === true || bookmarkData.success === undefined) {
        const formattedBookmarks = textUtils.formatBookmarksForUI(bookmarkData);
        setBookmarks(formattedBookmarks);
      } else {
        setBookmarks([]);
        // Don't show error for empty bookmarks, just show empty state
      }
    } catch (err) {
      setError('Failed to fetch bookmarks. Please try again.');
      setBookmarks([]);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRemoveBookmark = (queryId) => {
    setBookmarkToDelete(queryId);
    setShowConfirmModal(true);
  };

  const confirmDeleteBookmark = async () => {
    if (!bookmarkToDelete) return;

    const { userId, tenantId } = getUserAndTenantId();
    
    // Optimistically remove from UI
    setBookmarks(prevBookmarks => 
      prevBookmarks.filter(bookmark => bookmark.query_id !== bookmarkToDelete)
    );

    try {
      await bookmarkService.deleteBookmark(bookmarkToDelete, userId, tenantId);
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      // If delete fails, refresh to get current state
      refreshBookmarks();
    }

    setShowConfirmModal(false);
    setBookmarkToDelete(null);
  };

  const cancelDeleteBookmark = () => {
    setShowConfirmModal(false);
    setBookmarkToDelete(null);
  };

  const renderContent = () => {
    // Show loading while Teams context is being initialized
    if (teamsLoading) {
      return <LoadingSpinner />;
    }

    if (loading) {
      return <LoadingSpinner />;
    }

    if (bookmarks.length === 0) {
      return <EmptyState />;
    }

    return (
      <div className="bookmarks-grid">
        {bookmarks.map(bookmark => (
          <BookmarkCard
            key={bookmark.query_id}
            bookmark={bookmark}
            onRemove={handleRemoveBookmark}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="container">
      <Header onRefresh={refreshBookmarks} isRefreshing={isRefreshing} />
      
      <UserInfoDisplay 
        userInfo={userInfo}
        urlParams={urlUtils.getUrlParams()}
        teamsLoading={teamsLoading}
        teamsError={teamsError}
      />
      
      {renderContent()}

      <ConfirmationModal
        isVisible={showConfirmModal}
        onConfirm={confirmDeleteBookmark}
        onCancel={cancelDeleteBookmark}
      />
    </div>
  );
}

export default App;